import { forwardRef } from "react";
import { subtypeOf, useSections, specOf } from "@/lib/iwin";
import { parseGlass, cornerSectionDetails } from "@/lib/sections";
import { SheetFrame, F } from "@/components/sheets/SheetFrame";
import { Primitives } from "@/components/Primitives";
import { placeLabels, Leaders } from "@/components/sheets/Leaders";

const AREA = { x: 13, y: 30, w: 284, h: 234 };
const SCALES = [1, 2, 2.5, 3, 4, 5, 6, 8, 10];
const GAP = 3, HEAD = 7, MIN_W = 52, LBL = 58;
const STYLE = { ALU_PROFILE: ["#000000", 0.2], GASKET: ["#0E7490", 0.12], DIM: ["#A21CAF", 0.08], HATCH: ["#9CA3AF", 0.06], GLASS: ["#2563EB", 0.1], RAILING: ["#DC2626", 0.16], DETAIL: ["#6B7280", 0.08] };
const LEGEND = [["ALU_PROFILE", "Aluminium profile"], ["GASKET", "EPDM gasket"], ["GLASS", "Glass / infill"], ["DIM", "Dimensions (mm)"], ["RAILING", "Railing"]];

export function packDetails(details, ks) {
  const rows = [[]];
  let x = 0;
  for (const d of details) {
    const w = Math.max(d.w / ks + (d.generic ? LBL : 0), MIN_W), h = d.h / ks + HEAD;
    if (w > AREA.w) return null;
    if (x > 0 && x + w > AREA.w) { rows.push([]); x = 0; }
    rows[rows.length - 1].push({ d, w, h });
    x += w + GAP;
  }
  const hs = rows.map((r) => Math.max(...r.map((c) => c.h)));
  const spare = AREA.h - hs.reduce((a, b) => a + b, 0) - GAP * (rows.length - 1);
  if (spare < 0) return null;
  let y = AREA.y;
  return rows.flatMap((r, i) => {
    const extra = (AREA.w - r.reduce((a, c) => a + c.w, 0) - GAP * (r.length - 1)) / r.length, bh = hs[i] + spare / rows.length;
    let cx = AREA.x;
    const out = r.map((c) => { const o = { ...c, x: cx, y, bw: c.w + extra, bh }; cx += o.bw + GAP; return o; });
    y += bh + GAP;
    return out;
  });
}

const GenericBody = ({ c, ks }) => {
  const g = c.d.generic, dw = g.bounds.w / ks, dh = g.bounds.h / ks;
  const gx = c.x + 4, ox = gx - g.bounds.x / ks, oy = c.y + HEAD + (c.bh - HEAD - dh) / 2 - g.bounds.y / ks;
  const placed = placeLabels(g.labels, ox, oy, ks, c.y + HEAD + 1, c.y + c.bh - 2, 2);
  return (
    <g data-testid="iwin-corner-detail">
      <g transform={`translate(${ox} ${oy}) scale(${1 / ks})`}><Primitives prims={g.prims} u={0.17 * ks} palette="print" font={F} /></g>
      <Leaders placed={placed} labelX={gx + dw + 6} ts={2} max={40} />
    </g>
  );
};

const Detail = ({ c, i, ks, item }) => {
  const { d } = c, gx = c.x + (c.bw - d.w / ks) / 2, gy = c.y + HEAD + (c.bh - HEAD - d.h / ks) / 2;
  const title = d.label.startsWith("INTERLOCK STILE") && item.interlock ? `${d.label} · ${item.interlock}-TYPE SELECTED` : d.label;
  return (
    <g data-testid={`iwin-section-detail-${i}`}>
      <rect x={c.x} y={c.y} width={c.bw} height={c.bh} fill="none" stroke="#000" strokeWidth={0.25} />
      <circle cx={c.x + 4.5} cy={c.y + 4.2} r={2.6} fill="#0E7490" />
      <text x={c.x + 4.5} y={c.y + 5.25} fontSize={2.8} fill="#fff" fontFamily={F} textAnchor="middle" fontWeight="bold">{String.fromCharCode(65 + i)}</text>
      <text x={c.x + 8.8} y={c.y + 5.3} fontSize={2.5} fill="#000" fontFamily={F} fontWeight="bold">{title}</text>
      {d.generic ? <GenericBody c={c} ks={ks} /> : (
        <g transform={`translate(${gx} ${gy}) scale(${1 / ks})`}>
          {d.paths.map(([L, p]) => {
            const [col, sw] = STYLE[L] || STYLE.DETAIL;
            return <path key={L} d={p} data-layer={`IWIN_${L}`} fill="none" stroke={col} strokeWidth={sw * ks} strokeLinecap="round" />;
          })}
        </g>
      )}
    </g>
  );
};

const Footer = ({ s, item, ks }) => {
  const sp = specOf(s, item), dgu = parseGlass(item.glass_spec).panes.length > 1, bar = 100 / ks;
  const spec = `${s.name} · depth ${sp.frame_depth} mm · sightlines frame ${s.frame} / sash ${s.sash} / mullion ${s.mullion} mm · glass ${item.glass_spec || "—"} (${dgu ? "DGU 32" : "SGU 18"} mm groove)${item.interlock ? ` · ${item.interlock}-type interlock` : ""}`;
  return (
    <g data-testid="iwin-section-footer">
      {LEGEND.map(([L, t], i) => (
        <g key={L}>
          <line x1={14 + i * 38} y1={270} x2={22 + i * 38} y2={270} stroke={STYLE[L][0]} strokeWidth={0.6} />
          <text x={24 + i * 38} y={270.8} fontSize={2.2} fill="#000" fontFamily={F}>{t}</text>
        </g>
      ))}
      <text x={14} y={277.5} fontSize={2.3} fill="#000" fontFamily={F}>{spec.slice(0, 170)}</text>
      {[0, 1, 2, 3, 4].map((k) => <rect key={k} x={250 + (k * bar) / 5} y={268.5} width={bar / 5} height={1.6} fill={k % 2 ? "#fff" : "#000"} stroke="#000" strokeWidth={0.15} />)}
      {[["0", 0], ["50", bar / 2], ["100 mm", bar]].map(([t, dx]) => <text key={t} x={250 + dx} y={273.5} fontSize={2} fill="#000" fontFamily={F} textAnchor="middle">{t}</text>)}
    </g>
  );
};

export const IwinSectionSheet = forwardRef(({ item, tb, sheetNo, total }, ref) => {
  const s = subtypeOf(item), data = useSections(s);
  const details = data ? [...data.details, ...cornerSectionDetails(item).map((g) => ({ label: g.title, w: g.bounds.w, h: g.bounds.h, generic: g }))] : [];
  const ks = (details.length && SCALES.find((k) => packDetails(details, k))) || 10;
  const cells = details.length ? packDetails(details, ks) || [] : [];
  return (
    <SheetFrame svgRef={ref} item={item} tb={tb} sheetNo={sheetNo} total={total} suffix="S" kind="IWIN-S SECTION DETAILS (TRUE SCALE)"
      title="SECTION DETAILS · IWIN-S" subtitle={`EXACT SECTIONS FROM THE OFFICIAL BHORUKA / YKK AP DRAWING · ${s.name.toUpperCase()} · TRUE SCALE 1:${ks}`} scaleText={`1:${ks} @ A3`} scaleValue={ks}>
      {!data && <text x={AREA.x + AREA.w / 2} y={AREA.y + AREA.h / 2} fontSize={4} fill="#999" fontFamily={F} textAnchor="middle" data-testid="iwin-sections-loading">Loading exact IWIN-S sections…</text>}
      <g data-testid="iwin-sections" data-count={cells.length}>
        {cells.map((c, i) => <Detail key={i} c={c} i={i} ks={ks} item={item} />)}
      </g>
      {data && <Footer s={s} item={item} ks={ks} />}
      <text x={16} y={284.5} fontSize={2} fontFamily={F} fill="#555">
        Section geometry cut from the official IWIN-S vector drawing (bhorukaextrusions.com), calibrated to real millimetres. Verify with the latest Bhoruka / YKK AP technical documents.
      </text>
    </SheetFrame>
  );
});
IwinSectionSheet.displayName = "IwinSectionSheet";
